/**
 * +specs: fold a specialisation into its base class (docs/design/classes/specs.md "Framework").
 *
 * `applySpec(base, spec)` returns a new ClassDef: the base kit with every evolved ability swapped
 * on its key, the spec-only abilities bound, the spec's resources / passives / stats / weapon and
 * role. Pure and deterministic: the inputs are never mutated and the output depends only on them.
 * Gear, gems and talents (progression compileCharacter) apply on top of the result exactly like on
 * a base class, so a spec'd hero is just another ClassDef to the sim.
 */
import type { AbilityId, AuraDef, BindKey, ClassDef, ClassId, ExtraBindKey, HotbarKey, SpecDef } from '../contracts';
import { BIND_KEYS, EXTRA_BIND_KEYS } from '../contracts';

export interface ApplySpecOptions {
  /** Registry id of the result (default: the base class id). Use `specClassId(base, spec)` to register beside the base. */
  id?: ClassId;
}

const isBindKey = (k: HotbarKey): k is BindKey => (BIND_KEYS as readonly string[]).includes(k);
const isExtraKey = (k: HotbarKey): k is ExtraBindKey => (EXTRA_BIND_KEYS as readonly string[]).includes(k);

/** Swap evolved ability ids inside a kept passive (glows, counters, scoped mods, proc filters, overrides). */
function remapAura(a: AuraDef, evo: Readonly<Record<AbilityId, AbilityId>>): AuraDef {
  const m = (id: AbilityId): AbilityId => evo[id] ?? id;
  const out: AuraDef = structuredClone(a);
  if (out.mods) out.mods = out.mods.map((x) => (x.abilityIds ? { ...x, abilityIds: x.abilityIds.map(m) } : x));
  if (out.procs) out.procs = out.procs.map((p) => (p.abilityIds ? { ...p, abilityIds: p.abilityIds.map(m) } : p));
  if (out.overrides) out.overrides = out.overrides.map((o) => ({ abilityId: m(o.abilityId), with: m(o.with) }));
  if (out.ui) {
    const ui = { ...out.ui };
    if (ui.glow) ui.glow = { ...ui.glow, abilityIds: ui.glow.abilityIds.map(m) };
    if (ui.stackCounterOn) ui.stackCounterOn = ui.stackCounterOn.map(m);
    out.ui = ui;
  }
  return out;
}

/** Problems with a spec against its base class (empty = valid). Used by content tests and `applySpec`. */
export function specProblems(base: ClassDef, spec: SpecDef): string[] {
  const out: string[] = [];
  if (spec.baseClassId !== base.id && spec.baseClassId !== base.baseClassId) out.push(`${spec.id}: base class ${spec.baseClassId} ≠ ${base.id}`);
  const own = new Set(spec.abilities.map((a) => a.id));
  const baseIds = new Set(base.abilities.map((a) => a.id));
  const bound = [...Object.values(base.binds), ...Object.values(base.extraBinds ?? {})].filter((x): x is AbilityId => !!x);
  for (const id of bound) if (!spec.evolutions[id]) out.push(`${spec.id}: bound base ability ${id} has no evolution`);
  for (const [from, to] of Object.entries(spec.evolutions)) {
    if (!baseIds.has(from)) out.push(`${spec.id}: evolution from unknown base ability ${from}`);
    if (!own.has(to)) out.push(`${spec.id}: evolution ${from} → ${to} is not one of the spec's abilities`);
  }
  const keys = new Set<string>();
  for (const x of spec.extraAbilities) {
    if (!own.has(x.abilityId)) out.push(`${spec.id}: extra ${x.abilityId} is not one of the spec's abilities`);
    if (keys.has(x.key)) out.push(`${spec.id}: two spec abilities on key ${x.key}`);
    keys.add(x.key);
    const taken = isBindKey(x.key) ? base.binds[x.key] : isExtraKey(x.key) ? base.extraBinds?.[x.key] : undefined;
    if (taken && !spec.evolutions[taken]) out.push(`${spec.id}: key ${x.key} is already bound to ${taken}`);
  }
  const seen = new Set<string>();
  for (const a of spec.abilities) {
    if (seen.has(a.id)) out.push(`${spec.id}: duplicate ability ${a.id}`);
    seen.add(a.id);
    if (baseIds.has(a.id)) out.push(`${spec.id}: ability ${a.id} reuses a base ability id`);
  }
  return out;
}

/**
 * The base class with `spec` folded in. Idempotent for a def that already carries this spec.
 * Throws on a malformed spec (specProblems), so content tests catch it at build time.
 */
export function applySpec(base: ClassDef, spec: SpecDef, opts: ApplySpecOptions = {}): ClassDef {
  if (base.specId === spec.id) return opts.id && opts.id !== base.id ? { ...structuredClone(base), id: opts.id } : base;
  if (base.specId) throw new Error(`applySpec: ${base.id} already carries spec ${base.specId}`);
  const problems = specProblems(base, spec);
  if (problems.length) throw new Error(`applySpec: ${problems.join('; ')}`);
  const evo = spec.evolutions;
  const m = (id: AbilityId): AbilityId => evo[id] ?? id;

  const binds: ClassDef['binds'] = {};
  for (const [k, id] of Object.entries(base.binds)) if (id) binds[k as BindKey] = m(id);
  const extraBinds: NonNullable<ClassDef['extraBinds']> = {};
  for (const [k, id] of Object.entries(base.extraBinds ?? {})) if (id) extraBinds[k as ExtraBindKey] = m(id);
  for (const x of spec.extraAbilities) {
    if (isBindKey(x.key)) binds[x.key] = x.abilityId;
    else if (isExtraKey(x.key)) extraBinds[x.key] = x.abilityId;
  }

  // Abilities: unevolved base ones (Dodge, transform forms nobody evolved) + every spec ability.
  const abilities = [...base.abilities.filter((a) => !evo[a.id]).map((a) => structuredClone(a)), ...spec.abilities.map((a) => structuredClone(a))];
  const known = new Set(abilities.map((a) => a.id));
  const extraAbilities = [...new Set((base.extraAbilities ?? []).map(m))].filter((id) => known.has(id));

  // Unlocks: an evolved ability keeps its base level; spec-only abilities open at the spec's level.
  const unlocks: NonNullable<ClassDef['unlocks']> = {};
  for (const [id, lvl] of Object.entries(base.unlocks ?? {})) if (lvl !== undefined) unlocks[m(id)] = lvl;
  for (const x of spec.extraAbilities) unlocks[x.abilityId] = Math.max(unlocks[x.abilityId] ?? 0, spec.unlockLevel);

  const drop = new Set(spec.removePassives ?? []);
  const passives = [...(base.passives ?? []).filter((p) => !drop.has(p.id)).map((p) => remapAura(p, evo)), ...(spec.passives ?? []).map((p) => structuredClone(p))];

  const out: ClassDef = {
    ...structuredClone(base),
    id: opts.id ?? base.id,
    role: spec.role,
    resources: structuredClone(spec.resources ?? base.resources),
    binds,
    extraBinds,
    abilities,
    extraAbilities,
    passives,
    baseStats: { ...base.baseStats, ...(spec.baseStats ?? {}) },
    maxHp: spec.maxHp ?? base.maxHp,
    melee: spec.melee !== undefined ? structuredClone(spec.melee) : structuredClone(base.melee),
    color: spec.color ?? base.color,
    unlocks,
    specId: spec.id,
    baseClassId: base.baseClassId ?? base.id,
  };
  if (spec.weapon !== undefined) out.weapon = structuredClone(spec.weapon);
  return out;
}

/** The spec's every player-facing ability id in key order (evolved 1–7, then spec-only), for previews. */
export function specKit(base: ClassDef, spec: SpecDef): { key: HotbarKey; baseId: AbilityId | null; abilityId: AbilityId }[] {
  const out: { key: HotbarKey; baseId: AbilityId | null; abilityId: AbilityId }[] = [];
  const keys: HotbarKey[] = ['1', '2', '3', '4', '5', '6', '7'];
  for (const k of keys) {
    const id = isBindKey(k) ? base.binds[k] : isExtraKey(k) ? base.extraBinds?.[k] : undefined;
    if (id && spec.evolutions[id]) out.push({ key: k, baseId: id, abilityId: spec.evolutions[id]! });
  }
  for (const x of spec.extraAbilities) out.push({ key: x.key, baseId: null, abilityId: x.abilityId });
  return out;
}
